import type { GuideSnapGuide } from './types/guide-snap-guide'

/**
 * Computes the snap adjustment (dx/dy) needed to align `selectionBounds` to the nearest guide
 * within `snapGuideDistance`. Checks left, centerX, and right edges against vertical guides, and
 * top, centerY, and bottom edges against horizontal guides. Returns the smallest-magnitude
 * adjustment per axis, or 0 when no guide is within threshold.
 */
export function computeGuideSnapAdjustment(
  selectionBounds: { x: number; y: number; width: number; height: number },
  guides: ReadonlyArray<GuideSnapGuide>,
  snapGuideDistance: number
): { adjustX: number; adjustY: number } {
  const pickBestAdjust = (current: number | null, candidate: number): number | null => {
    if (!Number.isFinite(candidate)) return current
    if (Math.abs(candidate) > snapGuideDistance) return current
    if (current === null || Math.abs(candidate) < Math.abs(current)) return candidate
    return current
  }

  let bestAdjustX: number | null = null
  let bestAdjustY: number | null = null

  const left = selectionBounds.x
  const top = selectionBounds.y
  const right = left + selectionBounds.width
  const bottom = top + selectionBounds.height
  const centerX = left + selectionBounds.width / 2
  const centerY = top + selectionBounds.height / 2

  for (const guide of guides) {
    if (guide.type === 'vertical') {
      const gx = guide.position

      bestAdjustX = pickBestAdjust(bestAdjustX, gx - left)
      bestAdjustX = pickBestAdjust(bestAdjustX, gx - centerX)
      bestAdjustX = pickBestAdjust(bestAdjustX, gx - right)
    } else {
      const gy = guide.position

      bestAdjustY = pickBestAdjust(bestAdjustY, gy - top)
      bestAdjustY = pickBestAdjust(bestAdjustY, gy - centerY)
      bestAdjustY = pickBestAdjust(bestAdjustY, gy - bottom)
    }
  }

  return { adjustX: bestAdjustX ?? 0, adjustY: bestAdjustY ?? 0 }
}
